import { type ChangeType, RELEASES, runsOf } from "../releaseNotes";
import { Dialog } from "./ui";

/**
 * About the app: what each release added, newest first (Tyler, 2026-10-01). Opened from the foot of
 * the sidebar. The notes themselves are in `releaseNotes.ts`.
 */

const HEADINGS: Record<ChangeType, string> = {
  "New Feature": "New features",
  Enhancement: "Enhancements",
  "Change in Behavior": "Changes in behavior",
  "Bug Fix": "Bug fixes",
  "Known Limitation": "Known limitations",
};

const longDate = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
});

/** A note, with the app's labels in bold. */
function Note({ text }: { text: string }) {
  return (
    <>
      {runsOf(text).map((run, i) =>
        run.label ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: the runs of one fixed note never reorder
          <strong key={i}>{run.text}</strong>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: the runs of one fixed note never reorder
          <span key={i}>{run.text}</span>
        ),
      )}
    </>
  );
}

export function AboutDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} title="About BreweryLogistics" wide>
      <div className="dialog-body about">
        <p className="muted">
          BreweryLogistics is built in slices, each adding one part of the brewery's work. Until the
          app has version numbers, each slice is a release. The newest is first.
        </p>
        {RELEASES.map((r) => (
          <section key={r.version} className="release">
            <h3>{r.version}</h3>
            <p className="release-meta">
              Released {longDate.format(new Date(`${r.date}T12:00:00`))} · For{" "}
              {r.audience.toLowerCase()}
            </p>
            <p>{r.summary}</p>
            {r.changes.map((c) => (
              <div
                key={c.type}
                className={`release-changes ${c.type === "Known Limitation" ? "limits" : ""}`}
              >
                <h4>{HEADINGS[c.type]}</h4>
                <ul>
                  {c.items.map((item) => (
                    <li key={item}>
                      <Note text={item} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        ))}
      </div>
    </Dialog>
  );
}
